import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useAuth, API } from "@/AuthContext";
import { ArrowLeft, FilePlus2, Download, Trash2, Edit2, Check, X, Sparkles, Receipt, Loader2, Plus } from "lucide-react";

export default function PatientDetail() {
  const { id } = useParams();
  const { t } = useAuth();
  const [params] = useSearchParams();
  const [patient, setPatient] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(params.get("session"));

  const load = () => Promise.all([
    axios.get(`${API}/patients/${id}`),
    axios.get(`${API}/sessions?patient_id=${id}`),
  ]).then(([p, s]) => {
    setPatient(p.data); setSessions(s.data);
    if (!active && s.data.length) setActive(s.data[0].session_id);
  }).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const current = sessions.find((x) => x.session_id === active);

  const downloadPDF = async (sessionId) => {
    const r = await axios.get(`${API}/sessions/${sessionId}/pdf`, { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `soap_${sessionId}.pdf`; a.click();
    window.URL.revokeObjectURL(url);
  };

  const removeSession = async (sid) => {
    if (!window.confirm("Delete?")) return;
    await axios.delete(`${API}/sessions/${sid}`);
    if (active === sid) setActive(null);
    load();
  };

  const handleSessionUpdate = (updated) => {
    setSessions((prev) => prev.map((s) => (s.session_id === updated.session_id ? updated : s)));
  };

  if (!patient) return <div className="text-ink-muted">{t.common.loading}</div>;

  return (
    <div className="space-y-6" data-testid="patient-detail">
      <Link to="/dashboard/patients" className="inline-flex items-center gap-2 text-sm text-ink-muted hover:text-ink" data-testid="back-to-patients">
        <ArrowLeft className="w-4 h-4" /> {t.patients.backToList}
      </Link>

      <div className="bg-surface border border-hairline rounded-2xl p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-pine font-semibold">{patient.gender || "—"} · {patient.date_of_birth || "—"}</div>
          <h1 className="mt-2 font-heading text-3xl tracking-tight">{patient.first_name} {patient.last_name}</h1>
          <div className="mt-2 text-ink-muted">{patient.diagnosis || "—"}</div>
          {patient.notes && <p className="mt-3 max-w-2xl text-sm text-ink-muted">{patient.notes}</p>}
        </div>
        <Link
          to={`/dashboard/sessions/new?patient=${patient.patient_id}`}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-pine text-bone text-sm font-medium hover:bg-pine-hover transition"
          data-testid="new-session-from-patient"
        >
          <FilePlus2 className="w-4 h-4" /> {t.patients.newSession}
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sessions list */}
        <div className="lg:col-span-4">
          <div className="bg-surface border border-hairline rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-hairline">
              <h3 className="font-heading text-lg">{t.patients.sessions}</h3>
            </div>
            {sessions.length === 0 ? (
              <div className="py-10 text-center text-ink-muted text-sm">{t.dash.noSessions}</div>
            ) : (
              <div className="divide-y divide-hairline">
                {sessions.map((s) => (
                  <button
                    key={s.session_id}
                    onClick={() => setActive(s.session_id)}
                    className={`w-full text-left px-5 py-4 transition ${active === s.session_id ? "bg-pine-50" : "hover:bg-pine-50/40"}`}
                    data-testid={`session-item-${s.session_id}`}
                  >
                    <div className="text-xs text-ink-muted">{new Date(s.created_at).toLocaleString()}</div>
                    <div className="mt-1 text-sm line-clamp-2">{s.soap?.assessment || "—"}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Active SOAP + Billing */}
        <div className="lg:col-span-8 space-y-6">
          {current ? (
            <>
              <SessionCard
                session={current}
                patient={patient}
                t={t}
                onUpdate={handleSessionUpdate}
                onPDF={() => downloadPDF(current.session_id)}
                onDelete={() => removeSession(current.session_id)}
              />
              <BillingPanel
                session={current}
                t={t}
                onUpdate={handleSessionUpdate}
              />
            </>
          ) : (
            <div className="bg-surface border border-hairline rounded-2xl p-10 text-center text-ink-muted">
              {t.dash.noSessions}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SessionCard({ session, patient, t, onUpdate, onPDF, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(session.soap);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  useEffect(() => { setDraft(session.soap); setEditing(false); }, [session.session_id, session.soap]);

  const saveEdit = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/sessions/${session.session_id}`, draft);
      onUpdate(r.data);
      setEditing(false);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(session.soap); setEditing(false); };

  return (
    <div className="bg-surface border border-hairline rounded-2xl">
      <div className="px-6 py-4 border-b border-hairline flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-pine">SOAP · {new Date(session.created_at).toLocaleString()}</div>
          <h3 className="mt-1 font-heading text-xl">{patient.first_name} {patient.last_name}</h3>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {savedFlash && (
            <span className="inline-flex items-center gap-1 text-xs text-pine font-semibold animate-pulse">
              <Check className="w-3.5 h-3.5" /> {t.session.saved}
            </span>
          )}
          {!editing ? (
            <>
              <button onClick={() => setEditing(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-hairline text-sm hover:bg-pine-50 transition" data-testid={`edit-soap-${session.session_id}`}>
                <Edit2 className="w-4 h-4" /> {t.session.edit}
              </button>
              <button onClick={onPDF} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-hairline text-sm hover:bg-pine-50 transition" data-testid={`pdf-${session.session_id}`}>
                <Download className="w-4 h-4" /> {t.session.pdf}
              </button>
              <button onClick={onDelete} className="p-2 rounded-full hover:bg-terracotta/10" data-testid={`del-session-${session.session_id}`}>
                <Trash2 className="w-4 h-4 text-terracotta" />
              </button>
            </>
          ) : (
            <>
              <button onClick={saveEdit} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pine text-bone text-sm font-medium hover:bg-pine-hover disabled:opacity-60 transition" data-testid="save-soap-edit">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {t.session.saveChanges}
              </button>
              <button onClick={cancel} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-hairline text-sm hover:bg-pine-50 transition" data-testid="cancel-soap-edit">
                <X className="w-4 h-4" /> {t.session.cancelEdit}
              </button>
            </>
          )}
        </div>
      </div>
      <SOAPGrid soap={editing ? draft : session.soap} t={t} editable={editing} onChange={setDraft} />
      <div className="px-6 py-4 border-t border-hairline">
        <div className="text-xs uppercase tracking-[0.18em] font-semibold text-ink-muted">Raw notes</div>
        <p className="mt-2 text-sm text-ink-muted whitespace-pre-wrap">{session.raw_notes}</p>
      </div>
    </div>
  );
}

function SOAPGrid({ soap, t, editable, onChange }) {
  const sections = [
    { key: "subjective", label: t.session.sectionS },
    { key: "objective", label: t.session.sectionO },
    { key: "assessment", label: t.session.sectionA },
    { key: "plan", label: t.session.sectionP },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-hairline border-b border-hairline">
      {sections.map((s, i) => (
        <div key={s.key} className={`p-6 ${i >= 2 ? "border-t border-hairline" : ""}`}>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-pine">{s.label}</div>
          {editable ? (
            <textarea
              value={soap[s.key] || ""}
              onChange={(e) => onChange({ ...soap, [s.key]: e.target.value })}
              rows={5}
              className="mt-3 w-full bg-bone border border-hairline rounded-lg p-3 text-sm leading-relaxed focus:outline-none focus:border-pine resize-none"
              data-testid={`edit-soap-${s.key}`}
            />
          ) : (
            <p className="mt-3 text-sm leading-relaxed whitespace-pre-wrap" data-testid={`soap-${s.key}`}>{soap[s.key] || "—"}</p>
          )}
        </div>
      ))}
    </div>
  );
}

function BillingPanel({ session, t, onUpdate }) {
  const [items, setItems] = useState(session.billing_codes || []);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // reset when session changes
  useEffect(() => {
    setItems(session.billing_codes || []);
    setDirty(false);
  }, [session.session_id, session.billing_codes]);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    setDirty(true);
  };

  const removeItem = (idx) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const addItem = () => {
    setItems((prev) => [...prev, { code: "", name: "", rationale: "" }]);
    setDirty(true);
  };

  const aiSuggest = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(`${API}/sessions/${session.session_id}/billing-codes`);
      const fresh = r.data.suggestions || [];
      setItems(fresh);
      setDirty(false);
      // Also propagate to parent session state
      onUpdate({ ...session, billing_codes: fresh });
    } catch (e) {
      alert("Failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setGenerating(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/sessions/${session.session_id}/billing-codes`, {
        suggestions: items,
      });
      const saved = r.data.suggestions || [];
      setItems(saved);
      setDirty(false);
      onUpdate({ ...session, billing_codes: saved });
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1800);
    } catch (e) {
      alert("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-surface border border-hairline rounded-2xl" data-testid="billing-panel">
      <div className="px-6 py-4 border-b border-hairline flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] font-semibold text-pine">
            <Receipt className="w-3.5 h-3.5" /> {t.session.billingTitle}
          </div>
          <p className="mt-1 text-sm text-ink-muted">{t.session.billingDesc}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {savedFlash && (
            <span className="inline-flex items-center gap-1 text-xs text-pine font-semibold animate-pulse">
              <Check className="w-3.5 h-3.5" /> {t.session.saved}
            </span>
          )}
          <button
            onClick={aiSuggest}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pine-50 text-pine text-sm font-medium hover:bg-pine-50/70 disabled:opacity-60 transition"
            data-testid="billing-ai-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {items.length === 0 ? t.session.billingAISuggest : t.session.billingAIRegen}
          </button>
          <button
            onClick={save}
            disabled={!dirty || saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pine text-bone text-sm font-medium hover:bg-pine-hover disabled:opacity-40 transition"
            data-testid="billing-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            {t.session.billingSave}
          </button>
        </div>
      </div>

      <div className="p-6">
        {generating && items.length === 0 ? (
          <div className="text-center py-10 text-ink-muted text-sm">
            <Loader2 className="w-5 h-5 mx-auto animate-spin text-pine" />
            <div className="mt-3">{t.session.billingLoading}</div>
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 text-ink-muted text-sm">{t.session.billingEmpty}</div>
        ) : (
          <div className="space-y-3" data-testid="billing-items">
            {/* Column headers */}
            <div className="hidden sm:grid sm:grid-cols-12 gap-3 px-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">
              <div className="sm:col-span-2">{t.session.billingColCode}</div>
              <div className="sm:col-span-3">{t.session.billingColName}</div>
              <div className="sm:col-span-6">{t.session.billingColRationale}</div>
              <div className="sm:col-span-1"></div>
            </div>

            {items.map((it, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-start p-3 rounded-xl bg-bone border border-hairline hover:border-pine/40 transition"
                data-testid={`billing-row-${i}`}
              >
                <input
                  value={it.code || ""}
                  onChange={(e) => updateItem(i, { code: e.target.value })}
                  placeholder="X0701"
                  className="sm:col-span-2 px-3 py-2 rounded-lg bg-surface border border-hairline focus:outline-none focus:border-pine font-mono text-sm font-semibold text-pine"
                  data-testid={`billing-code-${i}`}
                />
                <input
                  value={it.name || ""}
                  onChange={(e) => updateItem(i, { name: e.target.value })}
                  placeholder={t.session.billingColName}
                  className="sm:col-span-3 px-3 py-2 rounded-lg bg-surface border border-hairline focus:outline-none focus:border-pine text-sm font-medium"
                  data-testid={`billing-name-${i}`}
                />
                <textarea
                  value={it.rationale || ""}
                  onChange={(e) => updateItem(i, { rationale: e.target.value })}
                  placeholder={t.session.billingColRationale}
                  rows={1}
                  className="sm:col-span-6 px-3 py-2 rounded-lg bg-surface border border-hairline focus:outline-none focus:border-pine text-sm resize-none"
                  data-testid={`billing-rationale-${i}`}
                />
                <button
                  onClick={() => removeItem(i)}
                  className="sm:col-span-1 flex items-center justify-center p-2 rounded-lg hover:bg-terracotta/10 text-terracotta transition"
                  data-testid={`billing-del-${i}`}
                  aria-label="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </motion.div>
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={addItem}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-dashed border-hairline hover:border-pine hover:text-pine text-sm transition"
            data-testid="billing-add-btn"
          >
            <Plus className="w-4 h-4" /> {t.session.billingAdd}
          </button>
          <div className="text-xs text-ink-muted max-w-md text-right">{t.session.billingDisclaimer}</div>
        </div>
      </div>
    </div>
  );
}
